// ============================================================
// GeofenceConfigCard.js — CareRota geofence numbers per client (geofence v1).
// A2Z-ADMIN-ONLY by locked design (§4) — the provider never sees/touches
// these; this card is what lets the POST-TRIAL threshold tuning happen
// without a deploy. The two override TIMERS are v1.1 (nothing reads them
// yet) — shown read-only/greyed. Ranges per the design: radius 10–500m,
// accuracy 10–200m. Values sit at the trial-gated working defaults (50/50).
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Field from '../../components/Field';
import { toast } from '../../components/Toaster';
import { getClientGeofenceConfig, patchClientGeofenceConfig } from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function GeofenceConfigCard({ clientId }) {
  const [radius, setRadius] = useState(50);
  const [accuracy, setAccuracy] = useState(50);
  const [timers, setTimers] = useState({ primary: 180, secondary: 120 });
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientGeofenceConfig(clientId);
      setRadius(res.geofenceRadiusMeters);
      setAccuracy(res.geofenceAccuracyThreshold);
      setTimers({ primary: res.overridePrimaryTimerSec, secondary: res.overrideSecondaryTimerSec });
      setIsDefault(!!res.isDefault);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await patchClientGeofenceConfig(clientId, {
        geofenceRadiusMeters: Number(radius),
        geofenceAccuracyThreshold: Number(accuracy),
      });
      toast.success('Geofence config saved');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setSaving(false); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <form className="pc-form" onSubmit={submit}>
      {isDefault && <StatusMessage as="p" variant="empty" className="pc-state">This client is on the platform defaults (no per-client row yet) — saving creates one.</StatusMessage>}
      <Field
        label="Geofence radius (metres)"
        required
        help="The base acceptance radius around a manager-pinned anchor (10–500m). Working default 50m — TRIAL-GATED: retune after the real-device geolocation trial."
      >
        <input type="number" min={10} max={500} value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="geofence-radius" />
      </Field>
      <Field
        label="Accuracy threshold (metres)"
        required
        help="Fixes reporting worse accuracy than this widen the acceptance honestly rather than false-failing (10–200m). Working default 50m — trial-gated."
      >
        <input type="number" min={10} max={200} value={accuracy} onChange={(e) => setAccuracy(e.target.value)} data-testid="geofence-accuracy" />
      </Field>
      <Field
        label="Override timers (v1.1 — not yet active)"
        help="The 3-min primary / 2-min secondary on-call escalation choreography ships in v1.1 — nothing reads these yet."
      >
        <input type="text" value={`${timers.primary}s primary / ${timers.secondary}s secondary`} disabled data-testid="geofence-timers" />
      </Field>
      <div className="pc-actions">
        <button type="submit" className="pc-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
